import * as React from 'react';
import Collapse from '@mui/material/Collapse';
import { TransitionProps } from '@mui/material/transitions';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { useSpring, animated } from '@react-spring/web';
import { Box, Stack, Paper, Snackbar, Alert, SnackbarCloseReason, Typography, useTheme, alpha, Button } from '@mui/material';
import { AccountTree as AccountTreeIcon, Add as AddIcon,} from '@mui/icons-material';
import { DialogPlanoDeContas } from './DialogPlanoDeContas';
import { useDebounce } from '../../../shared/hooks';
import { PlanoDeContasService } from '../../../shared/services/api/cadastros/PlanoDeContasService';
import { LayoutBaseDePagina } from '../../../shared/layouts';

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });
  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

export const PlanoDeContas: React.FC = () => {
  const [ITEMS, setItems] = React.useState<TreeViewBaseItem[]>([]);
  const [lastSelectedItem, setLastSelectedItem] = React.useState<string | null>(null);
  const [label, setLabel] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [openMsg, setOpenMsg] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [msgType, setMsgType] = React.useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const { debounce } = useDebounce();
  const theme = useTheme();

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  React.useEffect(() => {
    debounce(() => {
      PlanoDeContasService.getAll()
        .then((result) => {
          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setItems([
              {
                id: 'root',
                label: 'PLANO DE CONTAS',
                children: result.data
              }
            ]);
          }
        });
    });
  }, []);

  const handleItemSelectionToggle = (
    event: React.SyntheticEvent,
    itemId: string,
    isSelected: boolean,
  ) => {
    if (isSelected) {
      setLastSelectedItem(itemId);
    }
  };

  const findLabelById = (itemId: string, items: TreeViewBaseItem[]): string | null => {
    for (const item of items) {
      if (item.id === itemId) {
        return item.label;
      } else if (item.children && item.children.length > 0) {
        const foundLabel = findLabelById(itemId, item.children);
        if (foundLabel) {
          return foundLabel;
        }
      }
    }
    return null;
  };

 
  const handleCreateClick = () => {
    if (!lastSelectedItem) {
      setMsgType('warning');
      setMsg('Selecione uma conta pai para criar uma nova conta');
      setOpenMsg(true);
      return;
    }
    
    setLabel('');
    setOpen(true);
  };

  const handleItemDoubleClick = () => {
    if (lastSelectedItem && lastSelectedItem !== 'root') {
      const foundLabel = findLabelById(lastSelectedItem, ITEMS);
      if (foundLabel) {
        setLabel(foundLabel);
        setOpen(true);
      }
    }
  };

  const handleItensEditar = (dados: { id: string; label: string }) => {
    const updateItemLabel = (items: TreeViewBaseItem[]): TreeViewBaseItem[] => {
      return items.map(item => {
        if (item.id === dados.id) {
          return { ...item, label: dados.label };
        } else if (item.children && item.children.length > 0) {
          return { ...item, children: updateItemLabel(item.children) };
        } else {
          return item;
        }
      });
    };
    setItems(updateItemLabel(ITEMS));
    setMsgType('success');
    setMsg('Conta editada com sucesso!');
    setOpenMsg(true);
  };
  
  const handleItensAdicionar = (dados: { id: string, label: string, children: TreeViewBaseItem[] }) => {
    const updateItemChildren = (items: TreeViewBaseItem[]): TreeViewBaseItem[] => {
      return items.map(item => {
        if (item.id === lastSelectedItem || (!lastSelectedItem && item.id === 'root')) {
          const newChildren = item.children ? [...item.children, dados] : [dados];
          return { ...item, children: newChildren };
        } else if (item.children && item.children.length > 0) {
          return { ...item, children: updateItemChildren(item.children) };
        } else {
          return item;
        }
      });
    };
  
    setItems(updateItemChildren(ITEMS));
    setMsgType('success');
    setMsg('Conta criada com sucesso!');
    setOpenMsg(true);
  };


  return (  
    <LayoutBaseDePagina
      titulo='Plano de Contas'
    >
      <Box
        gap={1}
        marginX={1}
        padding={2}
        display="flex"
        flexDirection="column"
        component={Paper}
        sx={{ 
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          minHeight: '60vh'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountTreeIcon color="primary" />
            <Typography variant="h6" color="primary" fontWeight="bold">
              Estrutura do Plano de Contas
            </Typography>
          </Box>
          
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              size="small"
            >
              Nova Conta
            </Button>
          </Box>
        </Box>

        <Snackbar 
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
          open={openMsg}  
          autoHideDuration={6000} 
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>

        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          <RichTreeView
            aria-label="plano-de-contas"
            defaultExpandedItems={['root']}
            sx={{ 
              overflowX: 'hidden',
              flexGrow: 1,
              '& .MuiTreeItem-root': {
                marginY: 0.5
              },
              '& .MuiTreeItem-content': {
                padding: '8px 12px',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.16)
                  }
                }
              }
            }}
            slotProps={{ 
              item: { 
                slots: { groupTransition: TransitionComponent } 
              } 
            }}
            items={ITEMS}
            onItemSelectionToggle={handleItemSelectionToggle}
            onDoubleClick={handleItemDoubleClick}
          />
        </Stack>

        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            💡 Dica: {' '}
            • Clique em &quot;Nova Conta&quot; para criar uma conta filha do item selecionado{' '}
            • Clique duas vezes em um item ou use o ícone de edição para modificar{' '}
            • Use o ícone de exclusão para remover a conta selecionada
          </Typography>
        </Box>

        <DialogPlanoDeContas
          id={lastSelectedItem!}
          label={label}  
          open={open} 
          disabled={lastSelectedItem === 'root'}
          onDadosEditar={handleItensEditar} 
          onDadosAdicionar={handleItensAdicionar}
          onClose={() => {
            setOpen(false);
            setLabel('');
          }}
        /> 
      </Box>
    </LayoutBaseDePagina>
  );
};